import type { CreatorRefresh } from "@/types/types";

/**
 * "Refresh creator data first" on the Launch button (campaigns.creator_refresh).
 * The backend queues the maintenance worker, which re-scrapes every roster
 * creator's followers / avg / median views and then launches the campaign
 * through the backend. Running stamps older than this are ignored so a lost
 * task can't show a campaign as refreshing forever.
 */
export const CREATOR_REFRESH_STALE_MS = 6 * 60 * 60_000;

/** The worker stamps the launch outcome LAST; wait this long for it after the refresh ends. */
const LAUNCH_GRACE_MS = 2 * 60_000;

/**
 * Where an in-flight run is: "refreshing" while the scrape runs, "launching"
 * once it ended but the launch outcome (launched_at / launch_error) is not
 * stamped yet. null = nothing in flight.
 */
export type CreatorRefreshPhase = "refreshing" | "launching";

export function creatorRefreshPhase(
  record?: CreatorRefresh | null
): CreatorRefreshPhase | null {
  if (!record) return null;
  if (record.status === "running") {
    const since = new Date(record.started_at).getTime();
    return Number.isFinite(since) && Date.now() - since < CREATOR_REFRESH_STALE_MS
      ? "refreshing"
      : null;
  }
  if (record.launched_at || record.launch_error || !record.finished_at) return null;
  const finished = new Date(record.finished_at).getTime();
  return Number.isFinite(finished) && Date.now() - finished < LAUNCH_GRACE_MS
    ? "launching"
    : null;
}

export function isRefreshingCreators(
  campaign?: { creator_refresh?: CreatorRefresh | null } | null
): boolean {
  return creatorRefreshPhase(campaign?.creator_refresh) !== null;
}

/** "12/80" while the worker reports progress; null before the count is known. */
export function creatorRefreshProgress(record?: CreatorRefresh | null): string | null {
  if (!record || typeof record.total !== "number") return null;
  return `${record.processed ?? 0}/${record.total}`;
}
